import React, { useState } from 'react';

/**
 * Displays namespace status, labels and pods list in the details panel.
 * If a pod already has system_id matching a child entry's code, shows the
 * child entry's name instead of the raw pod name, with a "linked" badge.
 * Clicking a linked pod row navigates to that child entry.
 */
const NamespaceDetails = ({ namespaceDetails, loading, theme, borderColor, children = [], allEntries = [], onChildClick }) => {
  const [podsExpanded, setPodsExpanded] = useState(false);
  if (loading) {
    return (
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${borderColor}` }}>
        <div style={{ fontWeight: 700, marginBottom: 8, color: theme.toolbar.text }}>Namespace</div>
        <div style={{ fontSize: 12, opacity: 0.6, color: theme.toolbar.text }}>Загрузка...</div>
      </div>
    );
  }
  if (!namespaceDetails) return null;

  const isDark = theme.name === 'dark';
  const ns = namespaceDetails.namespace;
  const pods = namespaceDetails.pods || [];

  if (!ns) return null;

  const isActive = ns.status === 'Active';
  const labels = ns.labels ? Object.entries(ns.labels) : [];

  const runningPods = pods.filter(p => p.status === 'Running' || p.phase === 'Running');

  // Build lookups
  const childByCode = {};
  for (const child of children) {
    if (child.code) childByCode[child.code] = child;
  }
  const allByCode = {};
  for (const entry of allEntries) {
    if (entry.code) allByCode[entry.code] = entry;
  }

  // Find the registry entry linked to a pod.
  // Strategy 1: discovery-assigned system_id → exact match against ANY registry entry (reliable).
  // Strategy 2: name prefix match → ONLY explicit children (avoids false positives from other entries).
  //   e.g. child "mcp-server" matches pod "mcp-server-7d9f4b-xyz"
  const findLinkedEntry = (pod) => {
    if (pod.system_id) {
      if (childByCode[pod.system_id]) return childByCode[pod.system_id];
      if (allByCode[pod.system_id]) return allByCode[pod.system_id];
    }
    for (const child of children) {
      if (!child.code || !pod.name) continue;
      if (pod.name === child.code || pod.name.startsWith(child.code + '-')) {
        return child;
      }
    }
    return null;
  };

  // Inverse: which children have no matching pod?
  const linkedChildCodes = new Set();
  for (const pod of pods) {
    const linked = findLinkedEntry(pod);
    if (linked?.code) linkedChildCodes.add(linked.code);
  }
  const unlinkedChildren = children.filter(c => c.code && !linkedChildCodes.has(c.code));

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${borderColor}` }}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: theme.toolbar.text }}>
        Namespace
      </div>
      <div style={{
        background: isDark ? '#1a1f2e' : '#f8fafc',
        padding: 12, borderRadius: 6,
        border: `1px solid ${borderColor}`,
        display: 'flex', flexDirection: 'column', gap: 10
      }}>
        {/* Name + status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.toolbar.text, fontFamily: 'monospace' }}>
            {ns.name}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 700,
            padding: '2px 8px', borderRadius: 4,
            background: isActive
              ? (isDark ? '#1e4d2b' : '#d1fae5')
              : (isDark ? '#4a3b3b' : '#fee2e2'),
            color: isActive
              ? (isDark ? '#4ade80' : '#065f46')
              : (isDark ? '#f87171' : '#991b1b')
          }}>
            {ns.status || 'Unknown'}
          </span>
        </div>

        {/* Pod summary counters */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, color: theme.toolbar.text }}>Всего подов</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: theme.toolbar.text }}>{pods.length}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, color: theme.toolbar.text }}>Running</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#4ade80' }}>{runningPods.length}</div>
          </div>
        </div>

        {/* Labels */}
        {labels.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.toolbar.text, marginBottom: 4, opacity: 0.8 }}>
              Лейблы
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {labels.map(([k, v]) => (
                <span key={k} style={{
                  fontSize: 10, padding: '2px 6px', borderRadius: 4,
                  background: isDark ? '#1e2a3a' : '#e0eaff',
                  color: isDark ? '#93c5fd' : '#1e40af',
                  fontFamily: 'monospace'
                }}>
                  {k}={v}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Unlinked subsystems — children with no matching pod */}
        {unlinkedChildren.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.toolbar.text, marginBottom: 6, opacity: 0.8 }}>
              Подсистемы без пода ({unlinkedChildren.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {unlinkedChildren.map(child => (
                <div
                  key={child.id}
                  onClick={onChildClick ? () => onChildClick(child) : undefined}
                  title={onChildClick ? `Перейти к записи: ${child.name}` : child.name}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '5px 8px', borderRadius: 4,
                    background: isDark ? '#0d1117' : '#ffffff',
                    border: `1px solid ${borderColor}`,
                    borderLeft: `3px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
                    cursor: onChildClick ? 'pointer' : 'default',
                  }}
                >
                  <div style={{
                    fontSize: 12, fontWeight: 500,
                    color: theme.toolbar.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {child.name}
                  </div>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                    background: isDark ? '#1f2937' : '#f3f4f6',
                    color: isDark ? '#9ca3af' : '#6b7280',
                    flexShrink: 0, marginLeft: 6,
                  }}>
                    NO POD
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pods list — collapsible */}
        {pods.length > 0 && (
          <div>
            <button
              onClick={() => setPodsExpanded(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', background: 'none', border: 'none', padding: '2px 0',
                cursor: 'pointer', color: theme.toolbar.text,
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.8 }}>
                Поды ({pods.length})
              </span>
              <span style={{ fontSize: 10, opacity: 0.5 }}>{podsExpanded ? '▲' : '▼'}</span>
            </button>
            {podsExpanded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflowY: 'auto', marginTop: 6 }}>
              {pods.map(pod => {
                const linkedChild = findLinkedEntry(pod);
                const isRunning = pod.status === 'Running' || pod.phase === 'Running';
                const isPending = pod.status === 'Pending' || pod.phase === 'Pending';
                const statusColor = isRunning ? '#4ade80' : isPending ? '#fbbf24' : '#ef4444';
                const isClickable = !!linkedChild && !!onChildClick;

                return (
                  <div
                    key={pod.id}
                    onClick={isClickable ? () => onChildClick(linkedChild) : undefined}
                    title={linkedChild ? `Перейти к записи: ${linkedChild.name}` : pod.name}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 8px', borderRadius: 4,
                      background: isDark ? '#0d1117' : '#ffffff',
                      border: `1px solid ${borderColor}`,
                      borderLeft: `3px solid ${statusColor}`,
                      cursor: isClickable ? 'pointer' : 'default',
                    }}
                  >
                    {/* Left: display name + pod name hint */}
                    <div style={{ overflow: 'hidden', flex: 1 }}>
                      <div style={{
                        fontSize: 12, fontWeight: linkedChild ? 600 : 400,
                        color: linkedChild ? (isDark ? '#93c5fd' : '#1e40af') : theme.toolbar.text,
                        fontFamily: 'monospace',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {linkedChild ? linkedChild.name : pod.name}
                      </div>
                      {linkedChild && (
                        <div style={{ fontSize: 10, opacity: 0.5, color: theme.toolbar.text, fontFamily: 'monospace', marginTop: 1 }}>
                          {pod.name}
                        </div>
                      )}
                    </div>

                    {/* Right: linked flag + status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, marginLeft: 8 }}>
                      {linkedChild ? (
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                          background: isDark ? '#1e3a5f' : '#dbeafe',
                          color: isDark ? '#60a5fa' : '#1e40af',
                        }}>
                          LINKED
                        </span>
                      ) : (
                        <span style={{
                          fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                          background: isDark ? '#1f2937' : '#f3f4f6',
                          color: isDark ? '#6b7280' : '#9ca3af',
                        }}>
                          FREE
                        </span>
                      )}
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                        background: isRunning
                          ? (isDark ? '#1e4d2b' : '#d1fae5')
                          : isPending
                            ? (isDark ? '#4d3b1e' : '#fef3c7')
                            : (isDark ? '#4a3b3b' : '#fee2e2'),
                        color: isRunning
                          ? (isDark ? '#4ade80' : '#065f46')
                          : isPending
                            ? (isDark ? '#fbbf24' : '#92400e')
                            : (isDark ? '#f87171' : '#991b1b')
                      }}>
                        {pod.status || pod.phase}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NamespaceDetails;
